// Step-5 people-discovery types, shared by the http layer and the UI.

export type PeopleRole = "landlord" | "tenant";

// The people-ranking persona's prompt task_key on the unified /leads/ai-prompt
// endpoint (mirrors the BE people_discovery.TASK_KEY_BY_ROLE map).
export const taskForRole = (
  role: PeopleRole
): "people_landlord" | "people_tenant" =>
  role === "landlord" ? "people_landlord" : "people_tenant";

// One person as Apollo's free People Search returns them (no email/LinkedIn/full
// surname - those are a step-7 enrichment job), plus the LLM ranking labels.
export interface PeopleCandidate {
  apolloId: string;
  firstName: string;
  lastNameMasked: string;
  title: string;
  orgName: string;
  hasEmail: boolean;
  hasDirectPhone: boolean;
  // "primary" | "secondary" | "sustainability" | "other" - priority band.
  band: string;
  // "reply_target" | "signer" | "other" - the single best of each is labelled.
  roleLabel: string;
  reasoning: string;
  modelOrder: number;
  promptRevision: number;
  // Operator's pick (persisted via PATCH /leads/<id>/people-selection).
  selected: boolean;
}

// One Apollo count query the search ran, surfaced so a zero result is legible
// ("domain tesco.com -> 0", "name 'Keel Toys' -> 0").
export interface PeopleSearchAttempt {
  // "domain" | "name".
  kind: string;
  query: string;
  totalEntries: number;
}

// One person Apollo returned that the AI ranking excluded (shown in the collapsed
// "eliminated" section), with the model's one-line reason why. `reasoning` may be
// empty for rows discovered before it shipped, or if the model excluded without
// giving a reason. `apolloId`/`hasEmail` are absent on rows discovered before the
// enrichment feature shipped (an excluded person is otherwise selectable + pullable).
export interface EliminatedPerson {
  apolloId?: string;
  firstName: string;
  lastNameMasked: string;
  title: string;
  orgName: string;
  hasEmail?: boolean;
  reasoning?: string;
}

// Normalised email-confidence bucket for a pulled contact, computed on the BE by
// confidence_from_status and persisted on each record - the FE renders this value
// directly (never re-derives it), so the two agree by construction. "undeliverable" is
// a known-bad Apollo status (bounced/unavailable/invalid) shown as a red danger chip.
export type EnrichmentConfidence =
  | "verified"
  | "likely"
  | "unverified"
  | "undeliverable"
  | "none";

// One person's PAID contact enrichment (Apollo People Match), keyed to the discovery
// apolloId. `matched: false` means Apollo had no record - stored so a re-pull skips it
// (skip-only, no re-charge); it carries the discovery name/title so the row still shows.
export interface EnrichedContact {
  apolloId: string;
  // Where the address came from. "hubspot" means we already held it and promoting this
  // person spent no Apollo credit; absent / "apollo" is the paid People Match reveal.
  emailSource?: "hubspot" | "apollo";
  // The CRM record behind a HubSpot-sourced address, so the row can read its history.
  hubspotContactId?: string | null;
  name: string;
  title: string;
  email: string | null;
  emailStatus: string | null;
  linkedinUrl: string | null;
  confidence: EnrichmentConfidence;
  matched: boolean;
  enrichedAt: string;
  // Fields Apollo supplied into a blank at promote time, keyed by our field name. This
  // is what the "Push to HubSpot" button offers to write back - never a value HubSpot
  // gave us in the first place.
  apolloFilled?: Record<string, string> | null;
  // Set once those fields have been pushed into HubSpot, so the button stops offering
  // work that has already been done.
  hubspotPushedAt?: string | null;
  hubspotPushedFields?: string[] | null;
}

// One role's pulled-contacts blob, stored under contactEnrichments[role].
export interface ContactEnrichmentRoleResult {
  contacts: EnrichedContact[];
  companyName: string;
  updatedAt: string;
}

// The lead's per-role pulled-contacts state (separate from contactCandidates so a
// re-discovery never wipes paid reveals).
export type ContactEnrichments = Partial<
  Record<PeopleRole, ContactEnrichmentRoleResult>
>;

// One role's discovery result blob, stored under contactCandidates[role].
export interface PeopleRoleResult {
  candidates: PeopleCandidate[];
  companyName: string;
  discoveredAt: string;
  promptRevision: number;
  apolloTotalEntries: number;
  sampled: boolean;
  // "complete" | "no_people" | "quota_exhausted" | "not_run". The last two are MARKER
  // blobs (empty candidates): "quota_exhausted" = Apollo throttled ("throttled - retry"),
  // "not_run" = the sequential bulk auto-run never reached this pair ("not run yet -
  // click Find people"). Both are written so the cell shows an informative state rather
  // than an empty result that reads as "found nobody".
  status: string;
  // Every Apollo search that ran (domain first if present, then the name passes) +
  // the domain used, if any. Optional for rows discovered before this shipped.
  attempts?: PeopleSearchAttempt[];
  domain?: string | null;
  // How that domain was found: "captured" (OSM website), "override" (operator-typed),
  // "ai" (model-resolved + Apollo-verified), or "name" (no domain - name search only).
  // Absent on rows discovered before AI domain resolution shipped.
  domainSource?: string | null;
  // People Apollo returned that the AI ranking excluded (collapsed by default).
  eliminated?: EliminatedPerson[];
  // AI-trace run_id for the ranking pass, so "view contact ranking trace" opens it from
  // the persisted role blob. null/absent when no ranking ran (no_people / a marker).
  traceRunId?: string | null;
  // AI-trace run_id for the AI domain-resolution pass (separate from the ranking trace),
  // so the "(found by AI - view trace)" link opens the domain lookup. null/absent unless
  // an AI domain lookup ran + was captured.
  domainTraceRunId?: string | null;
}

// The lead/row's per-role people-discovery state.
export type ContactCandidates = Partial<Record<PeopleRole, PeopleRoleResult>>;
